import { useState, useEffect, useRef, Component } from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';
import INPInteractionDiagnostics from './INPInteractionDiagnostics';
import { hasValidInpMeasurement } from '../../utils/inpMeasurementValidity';
import {
  inpCauseDescription,
  inpCauseRecommendationsFor,
  inpGenericCodeExampleLabel,
  longTaskCountDiscrepancy,
  validLongTasks,
  normalizeInpDiagnosticCause
} from '../../utils/causeContent';

class INPErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('INP Panel error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium">INP Analysis Error</span>
          </div>
          <p className="text-xs text-red-300">
            {this.state.error?.message || 'An error occurred while displaying INP diagnostics.'}
          </p>
          <button 
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-2 text-xs text-red-400 hover:text-red-300 underline"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const severityColors = {
  critical: 'bg-red-600/20 text-red-400 border-red-500/30',
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  info: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
};

const getSeverityColor = (severity) => {
  return severityColors[severity] || severityColors.info;
};

const severityLabels = {
  critical: { en: 'Critical', cs: 'Kritická' },
  high: { en: 'High', cs: 'Vysoká' },
  medium: { en: 'Medium', cs: 'Střední' },
  low: { en: 'Low', cs: 'Nízká' }
};

const causeLabels = {
  'long-tasks': { en: 'Long Tasks', cs: 'Dlouhé úlohy' },
  'input-delay': { en: 'Input Delay', cs: 'Zpoždění vstupu' },
  'heavy-handler': { en: 'Heavy Event Handler', cs: 'Těžký event handler' },
  'presentation-delay': { en: 'Presentation Delay', cs: 'Zpoždění vykreslení' },
  'excessive-dom': { en: 'Excessive DOM Size', cs: 'Příliš velký DOM' },
  'high-memory': { en: 'High Memory Usage', cs: 'Vysoké využití paměti' },
  'main-thread-blocking': { en: 'Main Thread Blocking', cs: 'Blokování main thread' }
};

// Normalize audit device labels (PHONE/DESKTOP/TABLET/ALL or mobile/desktop)
// to what the deep-analysis backend understands.
const normalizeDeepDevice = (d) => {
  const v = String(d || '').toLowerCase();
  if (v === 'desktop') return 'desktop';
  // Tablet is never the prevailing device — inheriting it from the dashboard
  // context silently produced minority-device audits (4 % of users). Deep
  // runs default to mobile; tablet must be an explicit manual choice.
  return 'mobile'; // PHONE, mobile, tablet, ALL, unknown → mobile
};

const INPDiagnosticsPanelInner = ({ url, user, device, onResultsChange }) => {
  // Deep-run device defaults to the PRIORITY device from CrUX/GA4 device
  // priority (the most-used device for this origin) — fetched below. Until
  // that arrives, the dashboard context is a fallback (tablet never
  // pre-selects). A manual pick by the user always wins.
  const [deepDevice, setDeepDevice] = useState(normalizeDeepDevice(device));
  const [priorityDevice, setPriorityDevice] = useState(null);
  const userPickedDevice = useRef(false);

  useEffect(() => {
    if (!url) return;
    // New URL = new audit context: manual device pick belongs to the previous
    // origin, so re-enable auto-preselect of the priority device.
    userPickedDevice.current = false;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/lab-audits/device-priority', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ url })
        });
        const json = await res.json().catch(() => ({}));
        const top = json?.data?.items?.[0]?.device;
        if (!cancelled && top) {
          const dev = top === 'phone' ? 'mobile' : top; // phone/desktop/tablet
          setPriorityDevice(dev);
          if (!userPickedDevice.current) setDeepDevice(dev);
        }
      } catch { /* non-fatal — keep fallback default */ }
    })();
    return () => { cancelled = true; };
  }, [url]);
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [multiPageLoading, setMultiPageLoading] = useState(false);
  const [multiPageResults, setMultiPageResults] = useState(null);
  const [expandedPageIndex, setExpandedPageIndex] = useState(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  // Default ON: clicking "Accept all" measures the post-consent tracker layer,
  // which is what real users experience — a no-CMP run understates INP.
  const [skipCmp, setSkipCmp] = useState(true);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareResults, setCompareResults] = useState(null);
  
  const hasDeepAccess = user?.tier === 'premium' || user?.tier === 'enterprise' || user?.is_admin;

  const runDiagnostics = async (retryCount = 0) => {
    if (!url || !user) return;
    
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000);
      
      const response = await fetch('/api/lab-audits/inp-diagnostics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url, skipCmp, device: deepDevice }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await response.json();
      if (data.upgrade_required) {
        setShowUpgradeModal(true);
        return;
      }
      if (data.success && data.data) {
        setResults(data.data);
        onResultsChange?.({ deepINP: data.data });
      } else {
        setError(data.error || (language === 'cs' ? 'Analýza selhala' : 'Analysis failed'));
      }
    } catch (err) {
      console.error('INP diagnostics error:', err);
      if ((err.name === 'TypeError' || err.name === 'AbortError') && retryCount < 1) {
        console.log('INP diagnostics: retrying after connection error...');
        await new Promise(r => setTimeout(r, 3000));
        return runDiagnostics(retryCount + 1);
      }
      const isConnectionError = err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError') || err.name === 'AbortError';
      setError(isConnectionError 
        ? (language === 'cs' ? 'Spojení se serverem bylo přerušeno. Server je pravděpodobně přetížen — zkuste to za chvíli znovu.' : 'Connection to server was lost. The server may be overloaded — please try again in a moment.')
        : err.message);
    } finally {
      setLoading(false);
    }
  };

  const runCompare = async () => {
    if (!url || !user) return;
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    setCompareLoading(true);
    setError(null);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8 * 60 * 1000);
      const response = await fetch('/api/lab-audits/inp-compare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      const data = await response.json();
      if (data.upgrade_required) {
        setShowUpgradeModal(true);
        return;
      }
      if (data.success && data.data) {
        setCompareResults(data.data);
        onResultsChange?.({ inpCompare: data.data });
      } else {
        setError(data.error || (language === 'cs' ? 'Porovnání selhalo' : 'Comparison failed'));
      }
    } catch (err) {
      console.error('INP compare error:', err);
      setError(err.name === 'AbortError'
        ? (language === 'cs' ? 'Porovnání trvalo příliš dlouho — zkuste to znovu.' : 'Comparison took too long — please try again.')
        : err.message);
    } finally {
      setCompareLoading(false);
    }
  };

  const runMultiPageINP = async () => {
    if (!url || !user) return;
    
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    
    setMultiPageLoading(true);
    try {
      const response = await fetch('/api/lab-audits/multi-page-inp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // Keep the crawl's consent state in sync with the deep run: the panel's
        // skipCmp toggle means "auto-click Accept all", i.e. cmpMode 'accept'.
        body: JSON.stringify({ url, maxPages: 10, cmpMode: skipCmp ? 'accept' : 'none' })
      });
      
      const data = await response.json();
      if (data.upgrade_required) {
        setShowUpgradeModal(true);
        return;
      }
      if (data.success && data.data) {
        setMultiPageResults(data.data);
        onResultsChange?.({ multiPageINP: data.data });
      }
    } catch (err) {
      console.error('Multi-page INP error:', err);
    } finally {
      setMultiPageLoading(false);
    }
  };

  const captureScreenshot = async () => {
    if (!url) return;
    
    setScreenshotLoading(true);
    try {
      const response = await fetch('/api/lab-audits/element-screenshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          url, 
          fullPage: true,
          viewport: { width: 1280, height: 800 }
        })
      });
      
      const data = await response.json();
      if (data.success && data.screenshot) {
        setScreenshot(data.screenshot);
      }
    } catch (err) {
      console.error('Screenshot error:', err);
    } finally {
      setScreenshotLoading(false);
    }
  };

  const resetResults = () => {
    setResults(null);
    setError(null);
    setMultiPageResults(null);
    setScreenshot(null);
    setCompareResults(null);
  };

  const formatMs = (ms) => {
    if (!ms || ms < 0) return '0ms';
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${Math.round(ms)}ms`;
  };

  const getINPStatus = (inp) => {
    if (inp <= 200) return { status: 'good', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (inp <= 500) return { status: 'needs-improvement', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    return { status: 'poor', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  // DevTools "expert mode" — optional last level, never the main path. The
  // panel leads with its own diagnosis; this collapsible gives a concrete
  // 4-step verification recipe built from the measured numbers ("look for a
  // ~174 ms task"), instead of a generic "profile it in DevTools".
  const renderDevtoolsExpert = () => {
    const wi = results?.worstInteraction;
    if (!wi) return null;
    const cs = language === 'cs';
    const inMs = Math.round(wi.inputDelay || 0);
    const prMs = Math.round(wi.processingTime || 0);
    const psMs = Math.round(wi.presentationDelay || 0);
    if (inMs + prMs + psMs <= 0) return null;
    const totMs = Math.round(wi.duration || (inMs + prMs + psMs));
    // Only-problematic invariant: no expert recipe when the measured INP is
    // in the good band — there is nothing to verify.
    const inpVal = Number.isFinite(Number(results?.inp)) ? Number(results.inp) : totMs;
    if (inpVal <= 200) return null;
    const iName = wi.name || 'click';
    const tgt = wi.target?.tagName
      ? `${String(wi.target.tagName).toLowerCase()}${wi.target.id ? `#${wi.target.id}` : ''}`
      : null;
    const dominant = prMs >= psMs && prMs >= inMs ? 'processing' : (psMs >= inMs ? 'presentation' : 'input');
    const domMs = dominant === 'processing' ? prMs : dominant === 'presentation' ? psMs : inMs;
    const hunt = cs
      ? (dominant === 'processing'
        ? `V Main threadu hledejte dlouhou úlohu přibližně ${domMs} ms — nehledejte všechno, hledejte tuto úlohu. Rozbalte ji (Event → Call Tree / Function Call) a podívejte se, který skript a funkce ji tvoří.`
        : dominant === 'presentation'
          ? `Problém není v JavaScriptovém zpracování — přibližně ${domMs} ms vzniká při vykreslování. Hledejte bloky Rendering (Layout, Recalculate Style) a Paint po skončení handleru a prvek, který vyvolává rozsáhlý layout/repaint.`
          : `Přibližně ${domMs} ms uplyne PŘED spuštěním handleru (input delay). Hledejte úlohu, která v Main threadu běžela v okamžiku interakce — to je viník, ne kód navázaný na kliknutí.`)
      : (dominant === 'processing'
        ? `In the Main thread, look for a long task of roughly ${domMs} ms — don't look at everything, look for this task. Expand it (Event → Call Tree / Function Call) and see which script and function make it up.`
        : dominant === 'presentation'
          ? `The problem is not JavaScript processing — roughly ${domMs} ms happens during rendering. Look for Rendering (Layout, Recalculate Style) and Paint blocks after the handler finishes, and the element that triggers a large layout/repaint.`
          : `Roughly ${domMs} ms passes BEFORE the handler starts (input delay). Look for the task that was running on the Main thread at the moment of interaction — that is the culprit, not the code attached to the click.`);
    const steps = cs
      ? ['Otevřete Chrome DevTools (F12) → záložka Performance.', 'Spusťte Record.', `Proveďte stejnou interakci: ${iName}${tgt ? ` na prvku ${tgt}` : ''}.`, 'Zastavte nahrávání a najděte blok interakce.', hunt]
      : ['Open Chrome DevTools (F12) → Performance tab.', 'Start recording.', `Perform the same interaction: ${iName}${tgt ? ` on element ${tgt}` : ''}.`, 'Stop the recording and find the interaction block.', hunt];
    return (
      <details className="rounded-lg border border-dashed border-slate-600 bg-slate-800/30">
        <summary className="cursor-pointer px-3 py-2 text-xs text-slate-300 select-none">
          {cs ? 'Ověření v DevTools — volitelný expertní krok' : 'DevTools verification — optional expert step'}
        </summary>
        <div className="px-3 pb-3 text-xs text-slate-300 space-y-2">
          <p className="text-slate-400">
            {cs
              ? `Nástroj vede vlastní diagnózu výše; tento postup slouží jen k ručnímu ověření. Nejproblematičtější interakce: ${iName}${tgt ? ` (${tgt})` : ''} — INP ${totMs} ms (vstup ${inMs} ms, zpracování ${prMs} ms, vykreslení ${psMs} ms).`
              : `The tool leads its own diagnosis above; this recipe is only for manual verification. Worst interaction: ${iName}${tgt ? ` (${tgt})` : ''} — INP ${totMs} ms (input ${inMs} ms, processing ${prMs} ms, presentation ${psMs} ms).`}
          </p>
          <ol className="list-decimal pl-5 space-y-1">
            {steps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
          <p className="text-slate-500">
            {cs
              ? 'Pokud se místo očekávaného profilu objeví ten druhý (dlouhá JS úloha vs. Rendering/Layout/Paint), odpovídá to variabilitě mezi běhy — oba profily jsou reálné.'
              : 'If the other profile shows up instead (long JS task vs. Rendering/Layout/Paint), it matches the run-to-run variability — both profiles are real.'}
          </p>
        </div>
      </details>
    );
  };

  const renderInteractionBreakdown = () => {
    const interaction = results?.worstInteraction;
    if (!interaction) return null;

    const total = interaction.duration;
    const phases = [
      { name: language === 'cs' ? 'Zpoždění vstupu' : 'Input Delay', value: interaction.inputDelay, color: '#f59e0b' },
      { name: language === 'cs' ? 'Zpracování' : 'Processing', value: interaction.processingTime, color: '#8b5cf6' },
      { name: language === 'cs' ? 'Vykreslení' : 'Presentation', value: interaction.presentationDelay, color: '#ec4899' }
    ];

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-slate-400">
            {language === 'cs' ? 'Nejhorší interakce' : 'Worst Interaction'}
          </div>
          {!screenshot && (
            <button
              onClick={captureScreenshot}
              disabled={screenshotLoading}
              className="text-xs px-2 py-1 rounded bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 flex items-center gap-1 transition-colors disabled:opacity-50"
            >
              {screenshotLoading ? (
                <Icons.Loader size={12} className="animate-spin" />
              ) : (
                <Icons.Camera size={12} />
              )}
              {language === 'cs' ? 'Screenshot' : 'Screenshot'}
            </button>
          )}
        </div>
        
        {screenshot && (
          <div className="rounded-lg overflow-hidden border border-purple-500/30 mb-2">
            <img 
              src={`data:image/png;base64,${screenshot}`} 
              alt="Page Screenshot" 
              className="w-full"
            />
          </div>
        )}
        
        <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
          <div className="flex items-center gap-2 mb-2">
            {Icons?.MousePointer ? (
              <Icons.MousePointer size={14} className="text-purple-400" />
            ) : (
              <span className="text-purple-400">🖱️</span>
            )}
            <span className="text-sm text-white font-medium">{interaction.name || 'click'}</span>
            {interaction.target && (
              <code className="text-xs text-cyan-300 bg-slate-700 px-1.5 py-0.5 rounded">
                {interaction.target.tagName?.toLowerCase()}
                {interaction.target.id && `#${interaction.target.id}`}
              </code>
            )}
            {interaction.loadState && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded ${interaction.loadState === 'complete' ? 'bg-green-500/20 text-green-300' : 'bg-amber-500/20 text-amber-300'}`}
                title={language === 'cs' ? 'Stav načítání stránky v okamžiku interakce' : 'Page load state at the moment of the interaction'}
              >
                {interaction.loadState === 'complete'
                  ? (language === 'cs' ? 'po načtení' : 'after load')
                  : (language === 'cs' ? 'během načítání' : 'during load')}
              </span>
            )}
          </div>
          <div className="h-4 flex rounded-lg overflow-hidden mb-2">
            {phases.map((phase, i) => {
              if (!phase.value || phase.value <= 0) return null;
              const percent = (phase.value / total) * 100;
              return (
                <div 
                  key={i}
                  className="flex items-center justify-center"
                  style={{ 
                    width: `${Math.max(percent, 10)}%`,
                    backgroundColor: phase.color
                  }}
                  title={`${phase.name}: ${formatMs(phase.value)}`}
                />
              );
            })}
          </div>
          <div className="flex justify-between text-xs">
            {phases.map((phase, i) => (
              <div key={i} className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: phase.color }} />
                <span className="text-slate-400">{phase.name}:</span>
                <span className="text-white">{formatMs(phase.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderAttribution = () => {
    const attr = results?.attribution;
    if (!attr) return null;
    if (attr.attributionFailed || results?.attributionValid === false) {
      const reason = results?.navigatedTo
        ? (language === 'cs' ? `měřená interakce navigovala na jinou stránku (${results.navigatedTo})` : `the measured interaction navigated to another page (${results.navigatedTo})`)
        : results?.attributionInvalidReason === 'dom-sanity'
        ? (language === 'cs' ? 'měření zachytilo jiný dokument (podezřele malý DOM)' : 'the run captured a different document (suspiciously small DOM)')
        : (language === 'cs' ? `příliš velká část blokujícího času (${Math.round((attr.undeterminedShare || 0) * 100)} %) nejde přiřadit konkrétnímu skriptu` : `too much blocking time (${Math.round((attr.undeterminedShare || 0) * 100)}%) cannot be attributed to a specific script`);
      return (
        <div className="p-3 rounded-lg bg-slate-800/50 border border-amber-500/30 space-y-1">
          <div className="text-xs uppercase tracking-wider text-cyan-400">
            {language === 'cs' ? 'Atribuce blokujícího času' : 'Blocking Time Attribution'}
          </div>
          <div className="text-xs text-amber-300">
            {language === 'cs' ? 'Atribuce selhala — ' : 'Attribution failed — '}{reason}.{' '}
            {language === 'cs' ? 'Rozdělení první/třetí strany nezobrazujeme, bylo by zavádějící. Zkuste analýzu spustit znovu.' : 'The first-/third-party split is not shown as it would be misleading. Try re-running the analysis.'}
          </div>
        </div>
      );
    }
    if (!attr.scriptBlockingMs) return null;
    // Bounded data currently has no producer-side contract linking it to the
    // representative interaction/source attempt. A payload boolean alone is
    // not proof, so every bounded payload remains diagnostic-only.
    if (attr.windowBounded !== false) {
      return (
        <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 space-y-1">
          <div className="text-xs uppercase tracking-wider text-cyan-400">
            {language === 'cs' ? 'Diagnostické okno skriptů' : 'Diagnostic script window'}
          </div>
          <div className="text-xs text-slate-400">
            {language === 'cs'
              ? `Diagnostická okna nejsou jednoznačně propojena s reprezentativní interakcí ani se zdrojovým pokusem rozpadu fází. Naměřených ${formatMs(attr.scriptBlockingMs)} práce skriptů proto neurčuje podíl třetích stran ani viníka této interakce.`
              : `The diagnostic windows are not explicitly linked to the representative interaction or the source attempt of the phase breakdown. The measured ${formatMs(attr.scriptBlockingMs)} of script work therefore does not determine the third-party share or culprit of that interaction.`}
          </div>
        </div>
      );
    }
    const tpPct = Math.round((attr.thirdPartyShare || 0) * 100);
    return (
      <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 space-y-2">
        <div className="text-xs uppercase tracking-wider text-cyan-400">
          {language === 'cs' ? 'Atribuce blokujícího času' : 'Blocking Time Attribution'}
        </div>
        <div className="text-[10px] text-slate-500">
          {attr.windowBounded === false
            ? (language === 'cs'
              ? 'Nepodařilo se zachytit interakci — čísla níže pokrývají celý běh včetně načtení stránky, ne jen interakční okno.'
              : 'No interaction was captured — the numbers below cover the whole run including page load, not just the interaction window.')
            : (language === 'cs'
              ? 'Měřeno pouze v okně kolem interakce — procenta se vztahují k blokujícímu času skriptů během interakce, ne k celému načtení stránky (Lighthouse).'
              : 'Measured only within the interaction window — percentages refer to script blocking time during the interaction, not the full page load (Lighthouse).')}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-slate-400">
            {language === 'cs' ? 'Skripty celkem:' : 'Total script time:'}{' '}
            <span className="text-white font-medium">{formatMs(attr.scriptBlockingMs)}</span>
          </span>
          <span className="text-slate-400">
            {language === 'cs' ? 'Třetí strany:' : 'Third-party:'}{' '}
            <span className={`font-medium ${tpPct > 60 ? 'text-red-400' : tpPct > 30 ? 'text-amber-400' : 'text-green-400'}`}>
              {formatMs(attr.thirdPartyBlockingMs)} ({tpPct}%)
            </span>
          </span>
        </div>
        <div className="h-2 rounded-full bg-slate-700 overflow-hidden">
          <div className="h-full bg-red-500" style={{ width: `${Math.min(tpPct, 100)}%` }} />
        </div>
        {(() => {
          // Single source of truth: name the SAME task that leads the
          // long-tasks table below — never a second field from a different
          // measurement API (two conflicting "worst" entries in one report).
          const topLt = (results.longTasks || []).slice().sort((a, b) => (b.duration || 0) - (a.duration || 0))[0];
          if (!topLt) return null;
          const nm = topLt.scripts?.[0]?.sourceURL || topLt.name || 'unknown';
          return (
            <div className="text-[11px] text-slate-400 break-all">
              {language === 'cs' ? 'Nejdéle blokující úloha:' : 'Longest blocking task:'}{' '}
              <span className="text-cyan-300">{nm}</span>{' '}
              <span className="text-amber-400">({formatMs(topLt.duration)})</span>
              <span className="ml-1 text-slate-500">
                {language === 'cs' ? '— stejný záznam vede i tabulku dlouhých úloh níže' : '— the same entry leads the long-tasks table below'}
              </span>
            </div>
          );
        })()}
        {(attr.outOfScopeMs > 0 || attr.undeterminedMs > 0) && (
          <div className="text-[10px] text-slate-500">
            {language === 'cs'
              ? `Mimo měřenou stránku (iframe apod.): ${Math.round(attr.outOfScopeMs || 0)}ms · nepřiřazeno: ${Math.round(attr.undeterminedMs || 0)}ms — do procent výše se nepočítá.`
              : `Outside the measured page (iframes etc.): ${Math.round(attr.outOfScopeMs || 0)}ms · unattributed: ${Math.round(attr.undeterminedMs || 0)}ms — excluded from the percentages above.`}
          </div>
        )}
      </div>
    );
  };

  const [expandedTaskIndex, setExpandedTaskIndex] = useState(null);

  const renderLongTasks = () => {
    const tasks = validLongTasks(results?.longTasks);
    if (!tasks.length) return null;

    const sortedTasks = [...tasks].sort((a, b) => b.duration - a.duration).slice(0, 10);
    const totalBlocking = tasks.reduce((sum, t) => sum + Math.max(0, t.duration - 50), 0);

    const discrepancy = longTaskCountDiscrepancy(results, language);
    return (
      <div className="space-y-2">
        {discrepancy && <p className="text-xs text-amber-300">{discrepancy}</p>}
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-amber-400">
            {language === 'cs' ? 'Dlouhé úlohy — celý běh' : 'Long Tasks — whole run'} ({tasks.length}
            {Array.isArray(results.longTasksInWindow) && !discrepancy
              ? (language === 'cs'
                ? `, z toho ${validLongTasks(results.longTasksInWindow).length} v interakčním okně`
                : `, ${validLongTasks(results.longTasksInWindow).length} within the interaction window`)
              : ''})
          </div>
          <span className="text-xs text-slate-500">
            {language === 'cs' ? 'Blokující čas dlouhých úloh (celý běh)' : 'Long-task blocking time (whole run)'}: <span className="text-red-400 font-medium">{formatMs(totalBlocking)}</span>
          </span>
        </div>
        <div className="space-y-1">
          {sortedTasks.map((task, i) => {
            const blockingTime = Math.max(0, task.duration - 50);
            const hasScripts = task.scripts && task.scripts.length > 0;
            const hasAttribution = task.attribution && task.attribution.length > 0 && task.attribution[0].containerSrc;
            const isExpanded = expandedTaskIndex === i;

            return (
              <div key={i} className="rounded bg-slate-800/50 overflow-hidden">
                <div 
                  className={`flex items-center gap-2 p-2 ${(hasScripts || hasAttribution) ? 'cursor-pointer hover:bg-slate-700/50' : ''}`}
                  onClick={() => (hasScripts || hasAttribution) && setExpandedTaskIndex(isExpanded ? null : i)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div 
                        className={`h-2 rounded ${task.duration > 100 ? 'bg-red-500' : 'bg-amber-500'}`}
                        style={{ width: `${Math.min((task.duration / 200) * 100, 100)}%`, minWidth: '10%' }}
                      />
                      <span className="text-xs text-white font-medium whitespace-nowrap">{formatMs(task.duration)}</span>
                      {blockingTime > 0 && (
                        <span className="text-[10px] text-red-400/70 whitespace-nowrap">+{formatMs(blockingTime)} block</span>
                      )}
                    </div>
                    {hasScripts && (
                      <div className="text-[10px] text-slate-500 mt-0.5 break-all leading-tight" title={task.scripts[0].sourceURL}>
                        {task.scripts[0].sourceURL}
                      </div>
                    )}
                    {!hasScripts && hasAttribution && (
                      <div className="text-[10px] text-slate-500 mt-0.5 break-all leading-tight" title={task.attribution[0].containerSrc}>
                        {task.attribution[0].containerSrc}
                      </div>
                    )}
                    {!hasScripts && !hasAttribution && task.name && task.name !== 'self' && (
                      <div className="text-[10px] text-slate-500 mt-0.5">{task.name}</div>
                    )}
                  </div>
                  <span className="text-[10px] text-slate-600 whitespace-nowrap">@ {formatMs(task.startTime)}</span>
                  {(hasScripts || hasAttribution) && (
                    <Icons.ChevronDown size={12} className={`text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  )}
                </div>
                {isExpanded && hasScripts && (
                  <div className="px-2 pb-2 space-y-1 border-t border-slate-700/50">
                    <div className="text-[10px] text-slate-500 mt-1">
                      {language === 'cs' ? 'Skripty způsobující blokování:' : 'Scripts causing blocking:'}
                    </div>
                    {task.scripts.map((script, si) => (
                      <div key={si} className="p-1.5 rounded bg-slate-900/50 text-[10px]">
                        <div className="flex items-center gap-1">
                          <Icons.Code size={10} className="text-amber-400 shrink-0" />
                          <a 
                            href={script.sourceURL} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-cyan-400 hover:text-cyan-300 break-all"
                            title={script.sourceURL}
                          >
                            {script.sourceURL}
                          </a>
                        </div>
                        {(script.sourceFunctionName || script.invoker) && (
                          <div className="text-slate-500 ml-3.5 mt-0.5">
                            {script.sourceFunctionName && <span>fn: <span className="text-slate-300">{script.sourceFunctionName}</span></span>}
                            {script.invoker && <span className="ml-2">invoker: <span className="text-slate-300">{script.invoker}</span></span>}
                          </div>
                        )}
                        {script.duration > 0 && (
                          <div className="text-slate-500 ml-3.5 mt-0.5">
                            {language === 'cs' ? 'Doba:' : 'Duration:'} <span className="text-amber-400">{formatMs(script.duration)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {isExpanded && !hasScripts && hasAttribution && (
                  <div className="px-2 pb-2 border-t border-slate-700/50">
                    <div className="text-[10px] text-slate-500 mt-1 mb-1">
                      {language === 'cs' ? 'Zdroj úlohy:' : 'Task source:'}
                    </div>
                    {task.attribution.map((attr, ai) => (
                      <div key={ai} className="p-1.5 rounded bg-slate-900/50 text-[10px]">
                        <span className="text-slate-500">{attr.containerType || 'window'}:</span>{' '}
                        <span className="text-cyan-400 break-all">{attr.containerSrc || 'same-origin'}</span>
                        {attr.containerId && <span className="text-slate-500 ml-1">#{attr.containerId}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderStats = () => {
    if (!results) return null;

    return (
      <div className="grid grid-cols-2 gap-2">
        <div className="p-2 rounded-lg bg-slate-800/50 border border-slate-700">
          <div className="text-xs text-slate-400">{language === 'cs' ? 'DOM elementy' : 'DOM Elements'}</div>
          <div className={`text-lg font-bold ${results.domSize > 1500 ? 'text-red-400' : results.domSize > 800 ? 'text-amber-400' : 'text-green-400'}`}>
            {results.domSize?.toLocaleString()}
          </div>
        </div>
        <div className="p-2 rounded-lg bg-slate-800/50 border border-slate-700">
          <div className="text-xs text-slate-400">{language === 'cs' ? 'Interakce' : 'Interactions'}</div>
          <div className="text-lg font-bold text-blue-400">
            {results.interactions?.length || 0}
          </div>
        </div>
      </div>
    );
  };

  const inpCauseStyles = {
    'long-tasks': { icon: Icons.Clock, color: 'text-red-400', bg: 'from-red-900/30 to-orange-900/30', border: 'border-red-500/30' },
    'input-delay': { icon: Icons.Zap, color: 'text-amber-400', bg: 'from-amber-900/30 to-yellow-900/30', border: 'border-amber-500/30' },
    'heavy-handler': { icon: Icons.Code, color: 'text-purple-400', bg: 'from-purple-900/30 to-pink-900/30', border: 'border-purple-500/30' },
    'presentation-delay': { icon: Icons.Monitor, color: 'text-pink-400', bg: 'from-pink-900/30 to-rose-900/30', border: 'border-pink-500/30' },
    'excessive-dom': { icon: Icons.Layout, color: 'text-orange-400', bg: 'from-orange-900/30 to-amber-900/30', border: 'border-orange-500/30' },
    'high-memory': { icon: Icons.Database, color: 'text-cyan-400', bg: 'from-cyan-900/30 to-blue-900/30', border: 'border-cyan-500/30' },
    'main-thread-blocking': { icon: Icons.AlertTriangle, color: 'text-red-400', bg: 'from-red-900/30 to-rose-900/30', border: 'border-red-500/30' }
  };

  const inpCauseDescriptions = {
    'long-tasks': {
      cs: 'Dlouhé úlohy byly pozorovány v diagnostickém rozsahu; bez profilu nejsou potvrzenou příčinou field INP.',
      en: 'Long tasks were observed in the diagnostic scope; without a profile they are not a confirmed cause of field INP.'
    },
    'input-delay': {
      cs: 'Mezi kliknutím a spuštěním event handleru je velké zpoždění — main thread je blokovaný.',
      en: 'There is significant delay between click and event handler start — main thread is blocked.'
    },
    'heavy-handler': {
      cs: 'Event handler (onClick, onChange apod.) trvá příliš dlouho — pravděpodobně provádí těžké výpočty nebo synchronní operace.',
      en: 'Event handler (onClick, onChange etc.) takes too long — likely performing heavy computation or synchronous operations.'
    },
    'presentation-delay': {
      cs: 'V zaznamenané interakci byla pozorována fáze presentation delay; sama neurčuje složitost layoutu, DOM ani konkrétní příčinu.',
      en: 'A presentation-delay phase was observed in the recorded interaction; the phase alone does not identify layout or DOM complexity or a concrete cause.'
    },
    'excessive-dom': {
      cs: 'Stránka má příliš mnoho DOM elementů, což zpomaluje layout kalkulace a event handling.',
      en: 'The page has too many DOM elements, slowing down layout calculations and event handling.'
    },
    'high-memory': {
      cs: 'Vysoké využití paměti JavaScriptem — může způsobit GC (garbage collection) pauzy blokující interakce.',
      en: 'High JavaScript memory usage — may cause GC (garbage collection) pauses blocking interactions.'
    },
    'main-thread-blocking': {
      cs: 'Main thread je blokovaný dlouhými úlohami při načítání stránky — interakce budou pomalé.',
      en: 'Main thread is blocked by long tasks during page load — interactions will be slow.'
    }
  };

  const inpCauseRecommendations = {
    'long-tasks': {
      cs: ['Rozdělte dlouhé úlohy pomocí setTimeout(fn, 0) nebo scheduler.yield()', 'Přesuňte těžké výpočty do Web Workers', 'Použijte requestIdleCallback() pro neurgentní práci'],
      en: ['Break up long tasks using setTimeout(fn, 0) or scheduler.yield()', 'Move heavy computation to Web Workers', 'Use requestIdleCallback() for non-urgent work']
    },
    'input-delay': {
      cs: ['Odložte nekritické skripty pomocí defer/async', 'Code-split a lazy-load JavaScript moduly', 'Minimalizujte third-party skripty na interaktivních stránkách'],
      en: ['Defer non-critical scripts with defer/async', 'Code-split and lazy-load JavaScript modules', 'Minimize third-party scripts on interactive pages']
    },
    'heavy-handler': {
      cs: ['Použijte debounce/throttle pro časté eventy (scroll, input)', 'Vyhněte se synchronním layout operacím (čtení + zápis DOM)', 'Přesuňte DOM manipulace do requestAnimationFrame'],
      en: ['Use debounce/throttle for frequent events (scroll, input)', 'Avoid forced synchronous layouts (read then write DOM)', 'Move DOM manipulation to requestAnimationFrame']
    },
    'presentation-delay': {
      cs: ['Minimalizujte layout thrashing (batch DOM čtení/zápisy)', 'Použijte CSS contain: layout style paint', 'Animujte pouze transform a opacity (GPU accelerated)'],
      en: ['Minimize layout thrashing (batch DOM reads/writes)', 'Use CSS contain: layout style paint', 'Only animate transform and opacity (GPU accelerated)']
    },
    'excessive-dom': {
      cs: ['Virtualizujte dlouhé seznamy (react-window, virtual-scroller)', 'Lazy-loadujte obsah pod foldem', 'Odstraňte nepoužívané DOM elementy'],
      en: ['Virtualize long lists (react-window, virtual-scroller)', 'Lazy-load below-the-fold content', 'Remove unused DOM elements']
    },
    'high-memory': {
      cs: ['Ukliďte event listenery a intervaly', 'Vyhněte se memory leakům v closures', 'Lazy-loadujte JavaScript moduly'],
      en: ['Clean up event listeners and intervals', 'Avoid memory leaks in closures', 'Lazy-load JavaScript modules']
    },
    'main-thread-blocking': {
      cs: ['Odložte nekritický JavaScript', 'Použijte code splitting a dynamické importy', 'Zvažte Partytown pro third-party skripty'],
      en: ['Defer non-critical JavaScript', 'Use code splitting and dynamic imports', 'Consider Partytown for third-party scripts']
    }
  };

  const inpCauseExamples = {
    'long-tasks': {
      bad: 'button.onclick = () => {\n  heavyComputation(); // 300ms blocking\n  updateUI();\n}',
      good: 'button.onclick = async () => {\n  await scheduler.yield();\n  heavyComputation();\n  await scheduler.yield();\n  updateUI();\n}'
    },
    'input-delay': {
      bad: '<script src="analytics.js"></script>\n<script src="chat-widget.js"></script>',
      good: '<script src="analytics.js" defer></script>\n<script src="chat-widget.js" async></script>'
    },
    'heavy-handler': {
      bad: 'input.oninput = (e) => {\n  filterAllItems(e.target.value); // runs every keystroke\n}',
      good: 'input.oninput = debounce((e) => {\n  filterAllItems(e.target.value);\n}, 150);'
    },
    'presentation-delay': {
      bad: '// Layout thrashing\nel.style.width = el.offsetWidth + 10 + "px";\nel.style.height = el.offsetHeight + 10 + "px";',
      good: '// Batched read then write\nconst w = el.offsetWidth;\nconst h = el.offsetHeight;\nrequestAnimationFrame(() => {\n  el.style.width = w + 10 + "px";\n  el.style.height = h + 10 + "px";\n});'
    },
    'excessive-dom': {
      bad: '<div class="list">\n  <!-- 5000 items rendered -->\n  <div class="item">...</div>\n</div>',
      good: '<VirtualList\n  items={items}\n  itemHeight={40}\n  renderItem={(item) => <Item {...item} />}\n/>'
    }
  };

  const renderCausesAndRecommendations = () => {
    if (!results?.causes || !Array.isArray(results.causes) || results.causes.length === 0) return null;

    return (
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-wider text-orange-400 flex items-center gap-1">
          {Icons?.Target ? <Icons.Target size={12} /> : <span>🎯</span>}
          {language === 'cs' ? 'Pozorovaní kandidáti a postup ověření' : 'Observed candidates & verification steps'}
        </div>
        {results.causes.filter(Boolean).map(normalizeInpDiagnosticCause).map((cause, i) => {
          const style = inpCauseStyles[cause.cause] || { icon: Icons.AlertTriangle, color: 'text-slate-400', bg: 'from-slate-800/50 to-slate-700/30', border: 'border-slate-600' };
          const IconComponent = style.icon || Icons.AlertTriangle;
          const severity = 'candidate';

          return (
            <div key={i} className={`p-4 rounded-lg bg-gradient-to-r ${style.bg} border ${style.border}`}>
              <div className={`text-sm font-medium ${style.color} mb-1 flex items-center gap-2`}>
                <IconComponent size={14} />
                {causeLabels[cause.cause]?.[language] || cause.cause}
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  severity === 'critical' ? 'bg-red-500/30 text-red-300' :
                  severity === 'high' ? 'bg-red-500/30 text-red-300' : 
                  severity === 'medium' ? 'bg-amber-500/30 text-amber-300' : 
                  'bg-slate-500/30 text-slate-300'
                }`}>
                   {language === 'cs' ? 'kandidát k ověření' : 'candidate to verify'}
                </span>
              </div>
              <p className="text-xs text-slate-300 mb-2">
                 {inpCauseDescription(cause, language)}
              </p>
              {cause.cause === 'long-tasks' && longTaskCountDiscrepancy(results, language) && (
                <p className="text-[11px] text-amber-300 mb-2">{longTaskCountDiscrepancy(results, language)}</p>
              )}
              {cause.cause === 'long-tasks' && cause.totalRunCount != null && cause.inWindowCount != null && !longTaskCountDiscrepancy(results, language) && (
                <p className="text-[11px] text-slate-400 mb-2 italic">
                  {language === 'cs'
                    ? `Pozorováno ${cause.totalRunCount} za celý běh a ${cause.inWindowCount} v užším diagnostickém okně. Okno není reprezentativním vzorkem celého běhu a počty samy neprokazují příčinu field INP.`
                    : `Observed ${cause.totalRunCount} across the whole run and ${cause.inWindowCount} in the narrower diagnostic window. The window is not a representative sample of the whole run, and the counts alone do not establish the cause of field INP.`}
                </p>
              )}
              {inpCauseRecommendationsFor(cause, language).length > 0 && (
                <div className="text-xs text-slate-400">
                  <span className="font-medium">{language === 'cs' ? 'Doporučení:' : 'Fix:'}</span>
                  <ul className="list-disc list-inside mt-1 space-y-0.5 ml-2">
                    {inpCauseRecommendationsFor(cause, language).map((rec, j) => (
                      <li key={j}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
              {inpCauseExamples[cause.cause] && (
                <div className="mt-3 space-y-2">
                  <div className="text-xs font-medium text-slate-400">
                     {language === 'cs' ? 'Obecný příklad až po ověření:' : 'Generic example after verification:'}
                  </div>
                   <div className="text-[10px] text-slate-500">{inpGenericCodeExampleLabel[language]}</div>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="p-2 rounded bg-red-900/20 border border-red-500/20">
                      <div className="text-[10px] text-red-400 mb-1 flex items-center gap-1">
                         <Icons.X size={10} /> {language === 'cs' ? 'Obecný rizikový vzor' : 'Generic risk pattern'}
                      </div>
                      <pre className="text-[11px] text-red-300 whitespace-pre-wrap break-all">
                        {inpCauseExamples[cause.cause].bad}
                      </pre>
                    </div>
                    <div className="p-2 rounded bg-green-900/20 border border-green-500/20">
                      <div className="text-[10px] text-green-400 mb-1 flex items-center gap-1">
                         <Icons.Check size={10} /> {language === 'cs' ? 'Možná varianta po potvrzení profilem' : 'Possible variant after profile confirmation'}
                      </div>
                      <pre className="text-[11px] text-green-300 whitespace-pre-wrap break-all">
                        {inpCauseExamples[cause.cause].good}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMultiPageResults = () => {
    if (!multiPageResults) return null;
    
    const { pages, summary } = multiPageResults;
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-indigo-400 flex items-center gap-1">
            <Icons.Globe size={12} /> {language === 'cs' ? 'Multi-page INP analýza' : 'Multi-Page INP Analysis'}
          </div>
          <button 
            onClick={() => setMultiPageResults(null)}
            className="text-xs text-slate-400 hover:text-white transition-colors"
            title={language === 'cs' ? 'Zavřít' : 'Close'}
          >
            <Icons.X size={14} />
          </button>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          <div className="p-2 rounded bg-green-900/30 border border-green-500/30 text-center">
            <div className="text-lg font-bold text-green-400">{summary.goodPages}</div>
            <div className="text-xs text-green-300">{language === 'cs' ? 'Dobré' : 'Good'}</div>
          </div>
          <div className="p-2 rounded bg-amber-900/30 border border-amber-500/30 text-center">
            <div className="text-lg font-bold text-amber-400">{summary.needsImprovementPages}</div>
            <div className="text-xs text-amber-300">{language === 'cs' ? 'K zlepšení' : 'Needs work'}</div>
          </div>
          <div className="p-2 rounded bg-red-900/30 border border-red-500/30 text-center">
            <div className="text-lg font-bold text-red-400">{summary.poorPages}</div>
            <div className="text-xs text-red-300">{language === 'cs' ? 'Špatné' : 'Poor'}</div>
          </div>
        </div>
        
        <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
          <div className="flex justify-between text-xs text-slate-400 mb-2">
            <span>
              {language === 'cs' ? 'Průměrné INP:' : 'Average INP:'} 
              <span className={`ml-1 font-bold ${summary.avgINP <= 200 ? 'text-green-400' : summary.avgINP <= 500 ? 'text-amber-400' : 'text-red-400'}`}>
                {formatMs(summary.avgINP)}
              </span>
            </span>
            <span>
              {language === 'cs' ? 'Rozsah:' : 'Range:'} 
              <span className="ml-1 text-white">{formatMs(summary.minINP)} - {formatMs(summary.maxINP)}</span>
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="p-2 rounded bg-slate-900/50 text-center">
              <div className="text-xs text-slate-400">{language === 'cs' ? 'Prům. DOM' : 'Avg DOM'}</div>
              <div className={`text-sm font-bold ${summary.avgDOMSize > 1500 ? 'text-red-400' : summary.avgDOMSize > 800 ? 'text-amber-400' : 'text-green-400'}`}>
                {summary.avgDOMSize?.toLocaleString()}
              </div>
            </div>
            <div className="p-2 rounded bg-slate-900/50 text-center">
              <div className="text-xs text-slate-400">{language === 'cs' ? 'Prům. Long Tasks' : 'Avg Long Tasks'}</div>
              <div className={`text-sm font-bold ${summary.avgLongTasks > 5 ? 'text-red-400' : summary.avgLongTasks > 2 ? 'text-amber-400' : 'text-green-400'}`}>
                {summary.avgLongTasks}
              </div>
            </div>
          </div>
          
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {[...pages].sort((a, b) => (b.inp || 0) - (a.inp || 0)).map((page, i) => {
              const pathMatch = page.url.match(/https?:\/\/[^/]+(.*)/);
              const path = pathMatch ? pathMatch[1] || '/' : page.url;
              const isExpanded = expandedPageIndex === i;
              const hasCauses = page.causes && page.causes.length > 0;
              const hasInteractionDiagnostics = !!page.interactionDiagnostics;
              const hasDetails = hasCauses || hasInteractionDiagnostics;
              
              return (
                <div key={i} className="rounded bg-slate-900/50 overflow-hidden">
                  <div 
                    className={`flex items-center justify-between p-2 ${hasDetails ? 'cursor-pointer hover:bg-slate-800/50' : ''}`}
                    onClick={() => hasDetails && setExpandedPageIndex(isExpanded ? null : i)}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {hasDetails && (
                        <Icons.ChevronRight 
                          size={12} 
                          className={`text-slate-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      )}
                      <span className="text-xs text-slate-300 break-all">{path}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">DOM: {page.domSize}</span>
                      <span className={`text-xs font-mono ${
                        page.status === 'good' ? 'text-green-400' : 
                        page.status === 'needs-improvement' ? 'text-amber-400' : 
                        page.status === 'error' ? 'text-slate-500' : 'text-red-400'
                      }`}>
                        {page.error ? 'Error' : formatMs(page.inp)}
                      </span>
                      <div className={`w-2 h-2 rounded-full ${
                        page.status === 'good' ? 'bg-green-500' : 
                        page.status === 'needs-improvement' ? 'bg-amber-500' : 
                        page.status === 'error' ? 'bg-slate-500' : 'bg-red-500'
                      }`} />
                    </div>
                  </div>
                  
                  {isExpanded && hasDetails && (
                    <div className="px-3 pb-2 pt-1 border-t border-slate-700/50">
                      {hasCauses && <>
                        <div className="text-xs text-slate-400 mb-1">
                          {language === 'cs' ? 'Diagnostičtí kandidáti:' : 'Diagnostic candidates:'}
                        </div>
                        <div className="flex flex-wrap gap-1">
                           {page.causes.filter(Boolean).map(normalizeInpDiagnosticCause).map((cause, j) => (
                             <span key={j} className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
                               {causeLabels[cause.cause]?.[language] || cause.cause}
                               <span className="ml-1 text-slate-500">
                                 · {language === 'cs' ? 'kandidát' : 'candidate'}
                               </span>
                            </span>
                          ))}
                        </div>
                        <div className="mt-2 text-xs text-slate-400">
                          Long tasks: <span className="text-amber-400">{page.longTasksCount}</span>
                        </div>
                      </>}
                      {hasInteractionDiagnostics && (
                        <div className="mt-2">
                          <INPInteractionDiagnostics
                            diagnostics={{ ...page.interactionDiagnostics, pageUrl: page.url }}
                            language={language}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {summary.worstPage && (
          <div className="p-2 rounded-lg bg-red-900/20 border border-red-500/30">
            <div className="text-xs text-red-400">
              {language === 'cs' ? 'Nejhorší stránka:' : 'Worst page:'} 
              <span className="ml-1 text-white">{summary.worstPage.url}</span>
              <span className="ml-2 font-bold">{formatMs(summary.worstPage.inp)}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const compareConfigLabels = {
    A: { cs: 'Mobil + přijmout cookies', en: 'Mobile + accept cookies' },
    B: { cs: 'Mobil + odmítnout cookies', en: 'Mobile + reject cookies' },
    C: { cs: 'Desktop + odmítnout cookies', en: 'Desktop + reject cookies' },
    D: { cs: 'Mobil + banner ponechán', en: 'Mobile + banner untouched' }
  };

  const isValidCompareRun = (s) => (s.valid != null ? !!s.valid : (!!s.success && (s.cmpMode === 'none' || !!s.cmpClicked)));

  const renderCompareResults = () => {
    if (!compareResults?.summary?.length) return null;
    const allValid = compareResults.summary.every(isValidCompareRun);
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-emerald-400 flex items-center gap-1">
            <Icons.BarChart2 size={12} />
            {language === 'cs' ? 'Porovnání 4 běhů' : '4-Run Comparison'}
          </div>
          <button
            onClick={() => setCompareResults(null)}
            className="text-xs text-slate-400 hover:text-white transition-colors"
            title={language === 'cs' ? 'Zavřít' : 'Close'}
          >
            <Icons.X size={14} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-400 border-b border-slate-700">
                <th className="text-left py-1.5 pr-2 font-medium">{language === 'cs' ? 'Konfigurace' : 'Configuration'}</th>
                <th className="text-right py-1.5 px-2 font-medium">INP</th>
                <th className="text-right py-1.5 px-2 font-medium">{language === 'cs' ? 'Long tasks' : 'Long tasks'}</th>
                <th className="text-right py-1.5 pl-2 font-medium">{language === 'cs' ? '3. strany' : '3rd-party'}</th>
              </tr>
            </thead>
            <tbody>
              {compareResults.summary.map((s) => {
                const valid = isValidCompareRun(s);
                return (
                <tr key={s.key} className={`border-b border-slate-800${valid ? '' : ' opacity-60'}`}>
                  <td className="py-1.5 pr-2">
                    <span className="text-slate-500 mr-1">{s.key}</span>
                    <span className="text-slate-300">{compareConfigLabels[s.key]?.[language] || `${s.device} / ${s.cmpMode}`}</span>
                    {s.cmpMode !== 'none' && s.success && !s.cmpClicked && (
                      <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-red-500/15 text-red-300" title={language === 'cs' ? 'Cookie lišta nebyla nalezena/kliknuta — běh neměří to, co jeho označení tvrdí' : 'Cookie banner not found/clicked — the run does not measure what its label claims'}>
                        {language === 'cs' ? 'běh nevalidní — CMP nenalezeno' : 'invalid run — CMP not found'}
                      </span>
                    )}
                  </td>
                  <td className={`text-right py-1.5 px-2 font-mono font-medium ${!valid ? 'text-slate-500' : getINPStatus(s.inp || 0).color}`}>
                    {!s.success ? (language === 'cs' ? 'chyba' : 'error') : !valid ? '—' : formatMs(s.inp)}
                  </td>
                  <td className="text-right py-1.5 px-2 text-slate-300">{valid ? s.longTasksCount : '—'}</td>
                  <td className="text-right py-1.5 pl-2 text-slate-300">
                    {valid && s.thirdPartyShare != null ? `${Math.round(s.thirdPartyShare * 100)}%` : '—'}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-500">
          {allValid
            ? (language === 'cs'
              ? 'Rozdíl mezi konfiguracemi ukazuje vliv souhlasu i zařízení dohromady — nelze z něj izolovat jen marketingové skripty. Každá konfigurace je jeden běh (ne medián) a výsledek může vyjít oběma směry — odmítnutí někdy vede k více dlouhým úlohám (CMP zůstává aktivní, fallback logika), ne méně. Orientační porovnání, ne kontrolovaný experiment.'
              : 'The differences between configurations reflect consent and device combined — marketing scripts alone cannot be isolated from them. Each configuration is a single run (not a median) and the result can go either way — rejecting sometimes produces more long tasks (CMP stays active, fallback logic), not fewer. An indicative comparison, not a controlled experiment.')
            : (language === 'cs'
              ? 'Část běhů není validní (cookie lišta nebyla nalezena, nebo běh selhal) — konfigurace proto nelze mezi sebou porovnávat. Nevalidní běhy jsou z interpretace vyloučeny.'
              : 'Some runs are invalid (the cookie banner was not found, or the run failed) — the configurations therefore cannot be compared. Invalid runs are excluded from interpretation.')}
        </p>
      </div>
    );
  };

  const renderCompareButton = () => (
    <button
      onClick={runCompare}
      disabled={compareLoading}
      className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
    >
      {compareLoading ? (
        <>
          <Icons.Loader size={14} className="animate-spin" />
          {language === 'cs' ? 'Porovnávám 4 konfigurace (může trvat několik minut)...' : 'Comparing 4 configurations (may take several minutes)...'}
        </>
      ) : (
        <>
          <Icons.BarChart2 size={14} />
          {language === 'cs' ? 'Porovnat 4 konfigurace (cookies × zařízení)' : 'Compare 4 configurations (cookies × device)'}
        </>
      )}
    </button>
  );

  if (!user) {
    return (
      <div className="text-center py-4 text-slate-400 text-sm">
        {language === 'cs' ? 'Pro INP diagnostiku se přihlaste' : 'Sign in for INP diagnostics'}
      </div>
    );
  }

  const resultSpread = results?.labRunSpread || null;
  const validInpMeasurement = hasValidInpMeasurement(results);
  const resultSeriesVerified = !!(resultSpread
    && validInpMeasurement
    && (resultSpread.verified === true || (resultSpread.verified == null && resultSpread.runs >= 5)));
  const resultSeriesRuns = resultSpread?.runs || (results ? 1 : 0);

  return (
    <div className="space-y-4">
      {!results ? (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex items-start gap-3">
              {Icons?.MousePointer ? (
                <Icons.MousePointer size={20} className="text-purple-400 mt-0.5" />
              ) : (
                <span className="text-purple-400">🖱️</span>
              )}
              <div>
                <div className="text-sm text-white font-medium">
                  {language === 'cs' ? 'Hloubková INP analýza' : 'Deep INP Analysis'}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  {language === 'cs' 
                    ? 'Simuluje interakce, měří odezvu a identifikuje long tasks blokující main thread.'
                    : 'Simulates interactions, measures responsiveness and identifies long tasks blocking the main thread.'}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex-1">
              <div className="text-sm text-white font-medium">
                {language === 'cs' ? 'Odkliknout cookie lištu (přijmout vše)' : 'Click cookie banner (accept all)'}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {language === 'cs' 
                  ? 'Automaticky přijme souhlas v běžných CMP (Didomi, Cookiebot, OneTrust) — měří se i vrstva trackerů po souhlasu, jako u reálného uživatele'
                  : 'Auto-accepts consent in common CMPs (Didomi, Cookiebot, OneTrust) — measures the post-consent tracker layer, like a real user'}
              </div>
            </div>
            <button
              onClick={() => setSkipCmp(!skipCmp)}
              className={`relative w-12 h-6 rounded-full transition-colors ${skipCmp ? 'bg-purple-600' : 'bg-slate-600'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${skipCmp ? 'left-7' : 'left-1'}`} />
            </button>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <div className="text-sm text-white font-medium">
                {language === 'cs' ? 'Zařízení hloubkového testu' : 'Deep test device'}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {priorityDevice
                  ? (language === 'cs'
                    ? `Předvyplněno podle prioritního zařízení vašich uživatelů (${priorityDevice === 'mobile' ? 'mobil' : priorityDevice}) — změňte jen vědomě`
                    : `Pre-filled with your users\u2019 priority device (${priorityDevice}) — change only deliberately`)
                  : (language === 'cs'
                    ? 'Předvyplněno podle kontextu dashboardu — zkontrolujte, že měříte převažující zařízení vašich uživatelů (zpravidla mobil)'
                    : 'Pre-filled from the dashboard context — make sure you measure your users\u2019 prevailing device (usually mobile)')}
              </div>
            </div>
            <select
              value={deepDevice}
              onChange={(e) => { userPickedDevice.current = true; setDeepDevice(e.target.value); }}
              className="bg-slate-700 text-white text-sm rounded-lg px-3 py-2 border border-slate-600"
            >
              <option value="mobile">{language === 'cs' ? 'Mobil' : 'Mobile'}</option>
              <option value="desktop">Desktop</option>
              <option value="tablet">Tablet</option>
            </select>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <button
            onClick={runDiagnostics}
            disabled={loading}
            className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <>
                {Icons?.Loader ? (
                  <Icons.Loader size={16} className="animate-spin" />
                ) : (
                  <span className="animate-spin">⏳</span>
                )}
                {language === 'cs' ? 'Analyzuji INP...' : 'Analyzing INP...'}
              </>
            ) : (
              <>
                {Icons?.Search ? (
                  <Icons.Search size={16} />
                ) : (
                  <span>🔍</span>
                )}
                {language === 'cs' ? 'Spustit hloubkovou INP analýzu' : 'Run Deep INP Analysis'}
                <span className="text-xs opacity-75">({skipCmp
                  ? (language === 'cs' ? 'cookie lišta: přijmout' : 'cookie banner: accept')
                  : (language === 'cs' ? 'bez CMP' : 'no CMP')})</span>
              </>
            )}
          </button>

          {!compareResults ? (
            <>
              {renderCompareButton()}
              <p className="text-[11px] text-slate-500 text-center">
                {language === 'cs'
                  ? 'Spustí 4 měření: mobil s přijatými cookies, mobil s odmítnutými, desktop s odmítnutými a mobil s ponechaným bannerem.'
                  : 'Runs 4 measurements: mobile with accepted cookies, mobile with rejected, desktop with rejected, and mobile with the banner untouched.'}
              </p>
            </>
          ) : (
            renderCompareResults()
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                data-testid="inp-measurement-summary"
                data-measurement-available={validInpMeasurement ? 'true' : 'false'}
                className={`px-3 py-1.5 rounded-lg ${resultSeriesVerified ? getINPStatus(results.inp).bg : 'bg-slate-700/70 border border-slate-600'}`}
              >
                <span className={`text-lg font-bold ${resultSeriesVerified ? getINPStatus(results.inp).color : 'text-slate-200'}`}>
                  INP: {validInpMeasurement
                    ? formatMs(results.inp)
                    : (language === 'cs' ? 'nezměřeno' : 'not measured')}
                </span>
                <span className="ml-2 text-xs text-slate-400 font-normal">
                  {!validInpMeasurement
                    ? (language === 'cs'
                      ? 'nebyla zachycena platná interakce'
                      : 'no valid interaction was captured')
                    : resultSeriesVerified
                    ? (language === 'cs'
                      ? `ověřený medián ~${resultSpread.median} ms · n=${resultSeriesRuns}`
                      : `verified median ~${resultSpread.median} ms · n=${resultSeriesRuns}`)
                    : resultSeriesRuns >= 2
                      ? (language === 'cs'
                        ? `~${resultSpread?.displayValue ?? resultSpread?.average ?? results.inp} ms · orientační · n=${resultSeriesRuns} · průměr běhů`
                        : `~${resultSpread?.displayValue ?? resultSpread?.average ?? results.inp} ms · indicative · n=${resultSeriesRuns} · run average`)
                      : (language === 'cs' ? 'jeden běh, orientační' : 'single run, indicative')}
                </span>
              </div>
              {validInpMeasurement && (
                <span className="text-xs text-slate-400">
                  {language === 'cs' ? 'Cíl: pod 200ms' : 'Target: under 200ms'}
                </span>
              )}
              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
                {results.device === 'desktop'
                  ? (language === 'cs' ? 'Desktop (emulace)' : 'Desktop (emulated)')
                  : results.device === 'tablet'
                  ? (language === 'cs' ? 'Tablet (emulace)' : 'Tablet (emulated)')
                  : (language === 'cs' ? 'Mobil (emulace)' : 'Mobile (emulated)')}
              </span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">
                {(results.skipCmp || results.cmpMode === 'accept')
                  ? (language === 'cs' ? 'souhlas udělen' : 'consent given')
                  : (language === 'cs' ? 'bez CMP' : 'no CMP')}
              </span>
              {/* Loud warning when the run measured a different device than
                  the users' #1 priority device — saying "tablet" is not
                  enough, the mismatch itself must be flagged. */}
              {priorityDevice && results.device && results.device !== priorityDevice && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40" title={language === 'cs'
                  ? `Prioritní zařízení vašich uživatelů je ${priorityDevice === 'mobile' ? 'mobil' : priorityDevice} — tento běh nemusí reprezentovat většinu návštěvníků`
                  : `Your users' priority device is ${priorityDevice} — this run may not represent the majority of visitors`}>
                  {language === 'cs'
                    ? `⚠ jiné než prioritní zařízení (${priorityDevice === 'mobile' ? 'mobil' : priorityDevice})`
                    : `⚠ not the priority device (${priorityDevice})`}
                </span>
              )}
            </div>
            <button
              onClick={resetResults}
              className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
            >
              {Icons?.RefreshCw ? (
                <Icons.RefreshCw size={12} />
              ) : (
                <span>🔄</span>
              )}
              {language === 'cs' ? 'Znovu' : 'Re-run'}
            </button>
          </div>

          {renderStats()}
          {validInpMeasurement && renderInteractionBreakdown()}
          <INPInteractionDiagnostics diagnostics={results?.interactionDiagnostics} language={language} />
          {validInpMeasurement && renderDevtoolsExpert()}
          {validInpMeasurement && renderAttribution()}
          {renderLongTasks()}
          {validInpMeasurement && renderCausesAndRecommendations()}

          <div className="border-t border-slate-700 pt-4">
            {!compareResults ? renderCompareButton() : renderCompareResults()}
          </div>

          <div className="border-t border-slate-700 pt-4">
            {!multiPageResults ? (
              <button
                onClick={runMultiPageINP}
                disabled={multiPageLoading}
                className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {multiPageLoading ? (
                  <>
                    <Icons.Loader size={14} className="animate-spin" />
                    {language === 'cs' ? 'Analyzuji více stránek...' : 'Analyzing multiple pages...'}
                  </>
                ) : (
                  <>
                    <Icons.Globe size={14} />
                    {language === 'cs' ? 'Analyzovat INP na více stránkách' : 'Analyze INP on multiple pages'}
                  </>
                )}
              </button>
            ) : (
              renderMultiPageResults()
            )}
            <p className="text-xs text-slate-500 mt-2 text-center">
              {language === 'cs' 
                ? 'Analyzuje až 10 stránek webu a porovná jejich INP hodnoty'
                : 'Analyzes up to 10 pages of the website and compares their INP values'}
            </p>
          </div>

          <div className="text-xs text-slate-500 text-center pt-2 border-t border-slate-700">
            {language === 'cs' 
              ? `Analýza dokončena za ${(results.duration / 1000).toFixed(1)}s`
              : `Analysis completed in ${(results.duration / 1000).toFixed(1)}s`}
          </div>
        </div>
      )}
      
      {showUpgradeModal && (
        <div className="cv-app-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-slate-900 border-2 border-purple-500/50 rounded-2xl p-6 max-w-md mx-4 shadow-2xl shadow-purple-500/20" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Icons.Zap size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'cs' ? 'Hloubková INP analýza' : 'Deep INP Analysis'}
              </h3>
              <p className="text-slate-400 mb-4">
                {language === 'cs' 
                  ? 'Hloubkové analýzy jsou dostupné pouze pro tarify Premium a Enterprise.'
                  : 'Deep analyses are available only for Premium and Enterprise plans.'}
              </p>
              <div className="space-y-2">
                <a href="/pricing" className="block w-full py-3 px-4 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium transition-all">
                  {language === 'cs' ? 'Zobrazit tarify' : 'View Plans'}
                </a>
                <button onClick={() => setShowUpgradeModal(false)} className="w-full py-2 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm transition-all">
                  {language === 'cs' ? 'Zavřít' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const INPDiagnosticsPanel = (props) => {
  if (!props) {
    console.error('[INPDiagnosticsPanel] No props provided');
    return null;
  }
  
  return (
    <INPErrorBoundary>
      <INPDiagnosticsPanelInner {...props} />
    </INPErrorBoundary>
  );
};

export default INPDiagnosticsPanel;
